import React, { useState } from "react";

interface ToggleSwitchProps {
  /** Initial state of the toggle */
  initialState?: boolean;
  /** Color when toggle is active (on) */
  activeColor?: string;
  /** Color when toggle is inactive (off) */
  inactiveColor?: string;
  /** Size of the toggle component */
  size?: "small" | "default" | "large";
  /** Optional label text */
  label?: string;
  /** Callback function when toggle state changes */
  onChange?: (isActive: boolean) => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Additional CSS class names */
  className?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  initialState = false,
  activeColor = "#75A7AD",
  inactiveColor = "gray-300",
  size = "default",
  label = "",
  onChange = () => {},
  disabled = false,
  className = "",
}) => {
  const [isToggled, setIsToggled] = useState<boolean>(initialState);
  
  const handleToggle = (): void => {
    if (disabled) return;
    
    const newState = !isToggled;
    setIsToggled(newState);
    onChange(newState);
  };
  
  // Size configurations
  const sizeClasses: Record<string, { toggle: string; knob: string; knobTranslate: string }> = {
    small: {
      toggle: "h-5 w-10",
      knob: "h-4 w-4",
      knobTranslate: isToggled ? "translate-x-5" : "translate-x-1",
    },
    default: {
      toggle: "h-8 w-16",
      knob: "h-6 w-6",
      knobTranslate: isToggled ? "translate-x-9" : "translate-x-1",
    },
    large: {
      toggle: "h-10 w-20",
      knob: "h-8 w-8",
      knobTranslate: isToggled ? "translate-x-11" : "translate-x-1",
    },
  };
  
  const currentSize = sizeClasses[size] || sizeClasses.default;
  
  return (
    <div className={`flex items-center ${className}`}>
      {label && (
        <span className={`mr-3 ${disabled ? "text-gray-400" : "text-gray-700"}`}>
          {label}
        </span>
      )}
      
  
      <button 
       type="button" 
        onClick={handleToggle}
        disabled={disabled}
        className={`relative inline-flex items-center rounded-full transition-colors duration-300 focus:outline-none ${
          isToggled ? `bg-[${activeColor}]` : `bg-${inactiveColor}`
        } ${currentSize.toggle} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        aria-pressed={isToggled}
        role="switch"
      >
        <span 
          className={`inline-block transform rounded-full bg-white shadow-md transition-transform duration-300 ${
            currentSize.knob
          } ${currentSize.knobTranslate}`} 
        />
      </button>
    </div>
  );
};

export default ToggleSwitch;

